import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Users, TrendingDown, TrendingUp, Handshake } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import type { WhoPayWhoViewProps } from "@/ui/view-props";
import { PlayerAvatar } from '@/components/PlayerAvatar';

export function WhoPayWhoNeonView({ playerTotals, transactions, targetUserId, isReadOnly }: WhoPayWhoViewProps) {
  const targetPrefix = isReadOnly ? `/public/${targetUserId}` : "";

  return (
    <div className="animate-fade-in flex flex-col gap-8 pb-20">
      
      {/* ── Header ──────────────────────────────────────── */}
      <div className="flex items-center gap-4 px-1">
        <Link to={isReadOnly ? `/public/${targetUserId}` : "/"} className="no-underline">
          <button 
            type="button" 
            className="neon-btn-secondary p-2 rounded-xl flex items-center justify-center border-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </Link>
        <div>
          <h1 className="text-2xl font-black text-white tracking-tight uppercase leading-none">
            Who Pays Who
          </h1>
          <div className="text-[10px] text-neon-muted font-black tracking-widest mt-1 flex items-center gap-1.5 uppercase">
            <span className="w-1 h-1 rounded-full bg-neon-cyan" />
            Current Unsettled Balances
          </div>
        </div>
      </div>

      {playerTotals.length === 0 ? (
        <div className="glass-card p-16 text-center flex flex-col items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-neon-green/5 border border-neon-green/20 flex items-center justify-center">
            <Handshake className="w-10 h-10 text-neon-green" />
          </div>
          <div>
            <div className="text-xl font-black text-white uppercase mb-2">Zero unsettled balances</div>
            <p className="text-sm text-neon-muted max-w-xs mx-auto">
              All sessions and expenses are settled up! Everyone is even.
            </p>
          </div>
          <Link to={isReadOnly ? `/public/${targetUserId}` : "/"} className="no-underline">
            <button type="button" className="neon-btn-primary px-8 py-4 font-black uppercase tracking-widest text-xs">
              Return Home
            </button>
          </Link>
        </div>
      ) : (
        <>
          {/* ── Transfer list ───────────────────────────────── */}
          {transactions.length > 0 ? (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2 px-1">
                <TrendingDown className="w-4 h-4 text-neon-rose" />
                <div className="text-[10px] font-black text-neon-muted uppercase tracking-widest">
                  Required Transfers ({transactions.length})
                </div>
              </div>
              <div className="flex flex-col gap-3">
                {transactions.map((t, i) => (
                  <div key={i} className="glass-card p-4 flex items-center gap-4 animate-slide-up" style={{ animationDelay: `${i * 50}ms` }}>
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <PlayerAvatar name={t.from} avatarUrl={t.fromAvatarUrl} size={28} className="border border-neon-rose/30" />
                      <span className="text-sm font-black text-neon-rose truncate">{t.from}</span>
                    </div>
                    
                    <ArrowRight className="w-4 h-4 text-neon-muted shrink-0" />
                    
                    <div className="flex items-center gap-2.5 flex-1 min-w-0">
                      <PlayerAvatar name={t.to} avatarUrl={t.toAvatarUrl} size={28} className="border border-neon-emerald/30" />
                      <span className="text-sm font-black text-neon-emerald truncate">{t.to}</span>
                    </div>
                    
                    <div className="text-base font-black text-white tabular-nums tracking-tighter shrink-0">
                      {formatCurrency(t.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="glass-card p-8 text-center border-dashed border-white/10">
              <p className="text-xs font-bold text-neon-muted uppercase tracking-widest italic">
                All balances are equalized. No transfers needed.
              </p>
            </div>
          )}

          {/* ── Current balances ────────────────────────────── */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 px-1">
              <TrendingUp className="w-4 h-4 text-neon-cyan" />
              <div className="text-[10px] font-black text-neon-muted uppercase tracking-widest">
                Global Net Balances
              </div>
            </div>
            <div className="glass-card overflow-hidden">
              <div className="flex flex-col">
                {playerTotals.map((pt, i) => (
                  <div 
                    key={i} 
                    className="flex items-center justify-between p-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <PlayerAvatar name={pt.name} avatarUrl={pt.avatar_url} size={36} />
                      <div className="flex flex-col">
                        <span className="text-sm font-black text-white">{pt.name}</span>
                        <span className="text-[10px] font-bold text-neon-muted uppercase tracking-tighter">Spent: {formatCurrency(pt.spent)}</span>
                      </div>
                    </div>
                    <div className={cn(
                      "text-base font-black tabular-nums tracking-tighter",
                      pt.net > 0 ? "text-neon-green" : pt.net < 0 ? "text-neon-crimson" : "text-neon-muted"
                    )}>
                      {pt.net >= 0 ? '+' : ''}{formatCurrency(pt.net)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 px-1">
            <span className="text-[10px] text-neon-muted font-bold italic">
              * Showing aggregate balances from all unsettled sessions and expenses.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
